import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { Observable } from 'rxjs/internal/Observable';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { ProcessLog } from 'src/app/user-identification/models/user-identification';
import { CameraService } from './camera.service';
import { CloudLogsService, LogType } from './cloud-logs.service';
import { ConfigService } from './config/config.service';
import { WebRTCStatus, WebRTCStreamingService } from './webrtc-streaming.service';

export interface IStreamingConfig {
	enableRecording?: boolean;
	recordingFormat?: 'webm' | 'mp4';
	videoBitrate?: number;
	audioBitrate?: number;
	autoReconnect?: boolean;
	maxReconnectAttempts?: number;
}

export enum StreamingStatus {
	IDLE = 'idle',
	STARTING = 'starting',
	ACTIVE = 'active',
	STOPPING = 'stopping',
	ERROR = 'error',
}

/** Último fallo de waitForStreamingReady (NIF-1091 correlación streaming ↔ PoL). */
export interface StreamingReadyFailure {
	atMs: number;
	message: string;
	hasLiveOutboundVideo: boolean;
	iceConnectionState: RTCIceConnectionState | null;
	streamingStatus: StreamingStatus;
}

@Injectable({
	providedIn: 'root',
})
export class StreamingManagementService {
	// #region Services
	private webRTCStreamingService = inject(WebRTCStreamingService);
	private cloudLogsService = inject(CloudLogsService);
	private cameraService = inject(CameraService);
	private configService = inject(ConfigService);
	// #endregion

	private isStreamingActiveSubject = new BehaviorSubject<boolean>(false);
	private streamingStatusSubject = new BehaviorSubject<StreamingStatus>(StreamingStatus.IDLE);
	private destroy$ = new Subject<void>();

	private defaultStreamingConfig: IStreamingConfig = {
		enableRecording: true,
		recordingFormat: 'webm',
		videoBitrate: 1000000, // 1 Mbps
		audioBitrate: 128000, // 128 kbps
		autoReconnect: true,
		maxReconnectAttempts: 3,
	};

	private currentStreamId: string | null = null; // Para preservar el streamId durante cambio de cámara
	private baseStreamId: string | null = null; // El streamId base sin sufijos
	private currentOperationStreamId: string | null = null; // El streamId de la operación actual (POL/OCR)
	private isRecordingActive = false; // Indica si la grabación local está activa
	/** True mientras `retryStreaming` restaura WHIP (OCR/POL no deben abrir otra tile MEDIA_SERVER). */
	private streamingRetryInProgress = false;
	/** Último rechazo de waitForStreamingReady (telemetría NIF-1091). */
	private lastStreamingReadyFailure: StreamingReadyFailure | null = null;
	/**
	 * NIF-1158: evita republicados concurrentes / thrash tras mute o nuevo getUserMedia.
	 * No afecta al happy path (solo se entra desde recuperación de disrupción).
	 */
	private cameraDisruptionRecoveryPromise: Promise<boolean> | null = null;
	private lastCameraDisruptionRecoveryAtMs = 0;
	private static readonly CAMERA_DISRUPTION_RECOVERY_COOLDOWN_MS = 5_000;

	// Named references para poder eliminarlos correctamente con removeEventListener
	private readonly onBeforeUnload = (): void => {
		if (this.isStreamingActive()) this.emergencyCleanup();
	};
	private readonly onUnload = (): void => {
		if (this.isStreamingActive()) this.emergencyCleanup();
	};

	constructor() {
		this.setupWebRTCStatusMonitoring();
		this.setupPageUnloadHandlers(); // Manejar cierre de página
	}

	public async startStreaming(deviceId: string, stream: MediaStream): Promise<boolean> {
		try {
			this.streamingStatusSubject.next(StreamingStatus.STARTING);

			// Validar stream antes de proceder
			if (!stream || !stream.active) {
				this.streamingStatusSubject.next(StreamingStatus.ERROR);
				this.cloudLogsService.sendCloudLogs(
					{ streaming: 'Invalid stream provided to startStreaming' },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
				return false;
			}

			// Verificar si required_recording está habilitado
			const isRequired = this.checkRecordingRequired();

			if (!isRequired) {
				this.streamingStatusSubject.next(StreamingStatus.IDLE);
				return false;
			}

			// Inicializar conexión WebRTC
			const success = await this.webRTCStreamingService.initializeConnection(
				stream,
				deviceId,
				this.getWebRTCConfig(),
				deviceId, // Pasar deviceId como streamId inicial
			);

			if (success) {
				this.isStreamingActiveSubject.next(true);
				this.streamingStatusSubject.next(StreamingStatus.ACTIVE);
				// Guardar el streamId de la operación (deviceId en este caso)
				this.currentOperationStreamId = deviceId;
				// Guardar el streamId WebRTC (será el mismo al inicio)
				this.currentStreamId = deviceId;

				// Iniciar grabación local si está habilitada
				if (this.defaultStreamingConfig.enableRecording) {
					this.cameraService.startRecording(stream);
					this.isRecordingActive = true;
				}

				return true;
			} else {
				this.streamingStatusSubject.next(StreamingStatus.ERROR);
				this.cloudLogsService.sendCloudLogs(
					{ streaming: 'Failed to start WebRTC streaming' },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
				return false;
			}
		} catch (error: any) {
			this.streamingStatusSubject.next(StreamingStatus.ERROR);
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error starting streaming: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			return false;
		}
	}

	/**
	 * Obtiene el streamId actual de la operación
	 */
	public getCurrentStreamId(): string | null {
		return this.currentOperationStreamId;
	}

	/**
	 * Fuerza la desconexión inmediata del WebRTC sin esperar a callbacks
	 * Se usa cuando stopStreaming está tardando demasiado
	 */
	public forceDisconnect(): void {
		try {
			this.isStreamingActiveSubject.next(false);
			this.streamingStatusSubject.next(StreamingStatus.IDLE);
			this.webRTCStreamingService.emergencyDisconnect();
		} catch (error) {
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error during forceDisconnect: ${error?.toString()}` },
				ProcessLog.FINISH,
				LogType.Error,
			);
		}
	}

	public async stopStreaming(): Promise<void> {
		try {
			this.streamingStatusSubject.next(StreamingStatus.STOPPING);

			// Detener WebRTC
			this.webRTCStreamingService.disconnect();

			// Detener grabación local si está activa
			if (this.defaultStreamingConfig.enableRecording && this.isRecordingActive) {
				try {
					await this.cameraService.stopRecording();
					this.isRecordingActive = false;
				} catch (recordingError: any) {
					this.isRecordingActive = false;
					this.cloudLogsService.sendCloudLogs(
						{ streaming: `Error stopping recording: ${recordingError?.toString()}` },
						ProcessLog.FINISH,
						LogType.Error,
					);
				}
			}

			this.isStreamingActiveSubject.next(false);
			this.streamingStatusSubject.next(StreamingStatus.IDLE);
			// Limpiar los streamIds
			this.currentStreamId = null;
			this.currentOperationStreamId = null;
		} catch (error: any) {
			this.streamingStatusSubject.next(StreamingStatus.ERROR);
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error stopping streaming: ${error?.toString()}` },
				ProcessLog.FINISH,
				LogType.Error,
			);
		}
	}

	public async updateStream(newStream: MediaStream): Promise<boolean> {
		if (!this.isStreamingActive()) return false;

		try {
			const success = await this.webRTCStreamingService.updateStream(newStream);
			if (!success) {
				this.cloudLogsService.sendCloudLogs(
					{ streaming: 'Failed to update stream' },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
			}
			return success;
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error updating stream: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			return false;
		}
	}

	/**
	 * NIF-1158: hay sesión OCR/POL con `required_recording` ya publicada (`currentOperationStreamId`).
	 * `baseStreamId` solo no basta (se setea al cargar el proceso, antes de WHIP).
	 */
	public expectsActiveRecordingSession(): boolean {
		return this.checkRecordingRequired() && !!this.currentOperationStreamId;
	}

	/**
	 * Marca streaming en ERROR sin destruir la cámara (mute sostenido / rebind fallido).
	 * La tile MEDIA_SERVER + RETRY la dispara el caller (p. ej. `video.component`).
	 */
	public markStreamingBrokenAfterCameraDisruption(reason: string): void {
		this.streamingStatusSubject.next(StreamingStatus.ERROR);
		this.isStreamingActiveSubject.next(false);
		this.cloudLogsService.sendCloudLogs(
			{ streamingCameraDisruption: { reason, action: 'mark_broken' } },
			ProcessLog.IN_PROCESS,
			LogType.Warning,
		);
	}

	/**
	 * NIF-1158: recupera WHIP tras disrupción de cámara (nuevo stream o track recuperado).
	 * Happy path: no se llama. Mute transient no llega aquí (gracia en `video.component`).
	 * 1) `replaceTrack` si la sesión sigue ACTIVE.
	 * 2) Si no, republicado in-place (sin clearError / remount).
	 */
	public async recoverAfterCameraDisruption(
		reason: 'cameraTrackMute' | 'cameraTrackEnded' | 'newStream',
		stream?: MediaStream | null,
	): Promise<boolean> {
		if (!this.expectsActiveRecordingSession()) {
			return true;
		}
		if (this.streamingRetryInProgress) {
			return true;
		}
		if (this.cameraDisruptionRecoveryPromise) {
			return this.cameraDisruptionRecoveryPromise;
		}

		const now = Date.now();
		if (
			now - this.lastCameraDisruptionRecoveryAtMs <
				StreamingManagementService.CAMERA_DISRUPTION_RECOVERY_COOLDOWN_MS &&
			this.isStreamingActive()
		) {
			return true;
		}

		this.cameraDisruptionRecoveryPromise = this.runCameraDisruptionRecovery(reason, stream).finally(() => {
			this.lastCameraDisruptionRecoveryAtMs = Date.now();
			this.cameraDisruptionRecoveryPromise = null;
		});
		return this.cameraDisruptionRecoveryPromise;
	}

	private async runCameraDisruptionRecovery(
		reason: 'cameraTrackMute' | 'cameraTrackEnded' | 'newStream',
		stream?: MediaStream | null,
	): Promise<boolean> {
		const activeStream = stream ?? this.cameraService.stream;
		if (!activeStream?.active) {
			this.markStreamingBrokenAfterCameraDisruption(`${reason}:missing_stream`);
			return false;
		}

		this.cloudLogsService.sendCloudLogs(
			{ streamingCameraDisruption: { reason, action: 'recover_start', wasActive: this.isStreamingActive() } },
			ProcessLog.IN_PROCESS,
			LogType.Warning,
		);

		try {
			if (this.isStreamingActive()) {
				const updated = await this.updateStream(activeStream);
				if (updated) {
					await this.webRTCStreamingService.waitForOutboundMedia(3_000);
					await this.restartRecordingOnStream(activeStream);
					this.cloudLogsService.sendCloudLogs(
						{ streamingCameraDisruption: { reason, action: 'soft_replace_ok' } },
						ProcessLog.IN_PROCESS,
						LogType.Info,
					);
					return true;
				}
			}

			const streamId = this.currentOperationStreamId ?? this.baseStreamId;
			if (!streamId) {
				this.markStreamingBrokenAfterCameraDisruption(`${reason}:missing_streamId`);
				return false;
			}

			this.streamingStatusSubject.next(StreamingStatus.STARTING);
			this.isStreamingActiveSubject.next(false);
			this.webRTCStreamingService.resetPeerConnectionForRepublish();
			const ok = await this.startStreaming(streamId, activeStream);
			if (!ok) {
				this.markStreamingBrokenAfterCameraDisruption(`${reason}:republish_failed`);
				return false;
			}
			await this.waitForStreamingReady(15_000);
			this.cloudLogsService.sendCloudLogs(
				{ streamingCameraDisruption: { reason, action: 'republish_ok' } },
				ProcessLog.IN_PROCESS,
				LogType.Info,
			);
			return true;
		} catch (error: any) {
			this.markStreamingBrokenAfterCameraDisruption(
				`${reason}:recover_error:${error?.message ?? error?.toString?.() ?? 'unknown'}`,
			);
			this.cloudLogsService.sendCloudLogs(
				{
					streamingCameraDisruption: {
						reason,
						action: 'recover_failed',
						error: error?.toString?.() ?? String(error),
					},
				},
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			return false;
		}
	}

	/**
	 * Cambia la cámara usando un stream completo (alternativo)
	 * Para casos donde ya tienes el stream del nuevo dispositivo
	 */
	public async switchCameraWithStream(newStream: MediaStream): Promise<boolean> {
		if (!this.isStreamingActive()) return false;

		try {
			// Usar updateStream para cambiar al nuevo stream
			return await this.updateStream(newStream);
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error switching camera stream: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			return false;
		}
	}

	/**
	 * Cambia el streamId de la operación actual
	 * Detiene el streaming WebRTC actual e inicia uno nuevo con el nuevo streamId
	 * pero manteniendo la misma cámara activa
	 */
	public async changeOperationStreamId(
		newStreamId: string,
		newConstraints?: MediaStreamConstraints,
	): Promise<boolean> {
		if (!this.isStreamingActive()) {
			return false;
		}

		try {
			// Preservar referencia al stream de cámara activo
			const stream = this.cameraService.stream;
			if (!stream || !stream.active) {
				this.cloudLogsService.sendCloudLogs(
					{
						streaming: 'changeOperationStreamId: aborted — camera stream missing or inactive',
						newStreamId,
						hasStream: !!stream,
						streamActive: stream?.active,
					},
					ProcessLog.IN_PROCESS,
					LogType.Warning,
				);
				return false;
			}

			// CRITICAL: Check if constraints require a different camera (facingMode change)
			// If so, we need to request a new stream instead of reusing the existing one
			if (newConstraints) {
				const videoTrack = stream.getVideoTracks()[0];
				const currentSettings = videoTrack?.getSettings();
				const newVideoConstraints = newConstraints.video as MediaTrackConstraints;

				// Extract facingMode from constraints (can be string or object with 'ideal' property)
				const extractFacingMode = (constraint: any): string | undefined => {
					if (typeof constraint === 'string') return constraint;
					if (constraint && typeof constraint === 'object' && 'ideal' in constraint) return constraint.ideal;
					return undefined;
				};

				const newFacingMode = extractFacingMode(newVideoConstraints?.facingMode);

				// Check if facingMode changed (e.g., OCR uses 'environment', POL uses 'user')
				if (newFacingMode && currentSettings?.facingMode !== newFacingMode) {
					return false;
				}
			}

			// Detener grabación local si está activa (antes de desconectar WebRTC)
			if (this.defaultStreamingConfig.enableRecording && this.isRecordingActive) {
				try {
					await this.cameraService.stopRecording();
					this.isRecordingActive = false;
				} catch (error) {
					this.isRecordingActive = false;
					this.cloudLogsService.sendCloudLogs(
						{ streaming: `Error stopping recording before streamId change: ${error?.toString()}` },
						ProcessLog.IN_PROCESS,
						LogType.Error,
					);
				}
			}

			// Desconectar SOLO el WebRTC SIN detener los tracks de la cámara
			this.webRTCStreamingService.partialDisconnect();

			// Espera más larga para asegurar limpieza completa (especialmente en conexiones lentas)
			await new Promise((resolve) => setTimeout(resolve, 500));

			// Verificar que el stream sigue activo después de la desconexión parcial
			if (!stream.active) {
				this.cloudLogsService.sendCloudLogs(
					{
						streaming: 'changeOperationStreamId: failed — stream inactive after partialDisconnect',
						newStreamId,
					},
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
				return false;
			}

			// Iniciar nuevo streaming con el nuevo streamId usando el MISMO stream de cámara
			// Implementar retry logic para manejar fallos temporales
			const webRTCConfig = this.getWebRTCConfig();
			let success = false;
			const maxRetries = 2;

			for (let attempt = 1; attempt <= maxRetries; attempt++) {
				success = await this.webRTCStreamingService.initializeConnection(
					stream,
					newStreamId,
					webRTCConfig,
					newStreamId,
				);

				if (success) break;

				this.cloudLogsService.sendCloudLogs(
					{
						streaming: 'changeOperationStreamId: initializeConnection failed, will retry if attempts left',
						newStreamId,
						attempt,
						maxRetries,
					},
					ProcessLog.IN_PROCESS,
					LogType.Warning,
				);

				if (attempt < maxRetries) {
					// Esperar antes del retry
					await new Promise((resolve) => setTimeout(resolve, 1000));
				}
			}

			if (!success) {
				this.cloudLogsService.sendCloudLogs(
					{
						streaming: 'changeOperationStreamId: failed after all initializeConnection retries',
						newStreamId,
						maxRetries,
					},
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
				return false;
			}

			// Reiniciar grabación local con el nuevo streamId
			if (this.defaultStreamingConfig.enableRecording) this.cameraService.startRecording(stream);

			// CRITICAL: Re-emit the stream to notify any waiting video components
			// This is needed for video components that were waiting during the streamId change
			this.cameraService.stream = stream;

			this.currentOperationStreamId = newStreamId;
			return true;
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error changing operation streamId: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			return false;
		}
	}

	/**
	 * Tras cambiar de cámara (mismo u otro MediaStream): detiene MediaRecorder anterior y reinicia en el stream actual.
	 * Necesario en OCR→POL cuando `handleCameraChangeIfNeeded` para tracks viejos sin pasar por `changeOperationStreamId`.
	 */
	public async restartRecordingOnStream(stream: MediaStream | null): Promise<void> {
		if (!this.checkRecordingRequired() || !this.defaultStreamingConfig.enableRecording) {
			return;
		}
		if (!stream || !stream.active) {
			this.cloudLogsService.sendCloudLogs(
				{
					streaming: 'restartRecordingOnStream: invalid stream, skipping',
					hasStream: !!stream,
					active: stream?.active,
				},
				ProcessLog.IN_PROCESS,
				LogType.Warning,
			);
			return;
		}
		if (this.isRecordingActive) {
			try {
				await this.cameraService.stopRecording();
			} catch (e: any) {
				this.cloudLogsService.sendCloudLogs(
					{ streaming: `restartRecordingOnStream: stopRecording error: ${e?.toString()}` },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
			}
			this.isRecordingActive = false;
		}
		this.cameraService.startRecording(stream);
		this.isRecordingActive = true;
	}

	public isStreamingActive(): boolean {
		return this.isStreamingActiveSubject.value;
	}

	public getStreamingStatus(): Observable<StreamingStatus> {
		return this.streamingStatusSubject.asObservable();
	}

	public getCurrentStreamingStatus(): StreamingStatus {
		return this.streamingStatusSubject.value;
	}

	public getStreamingActiveStatus(): Observable<boolean> {
		return this.isStreamingActiveSubject.asObservable();
	}

	/**
	 * Espera a que el streaming esté completamente activo y listo para operaciones
	 * @param timeoutMs Tiempo máximo de espera en milisegundos (default: 15000)
	 * @returns Promise que se resuelve cuando el streaming está activo o rechaza si hay timeout
	 */
	public async waitForStreamingReady(timeoutMs: number = 15000): Promise<void> {
		try {
			const startedAt = Date.now();
			await this.waitForWebRtcConnected(timeoutMs);
			const remainingMs = timeoutMs - (Date.now() - startedAt);
			if (remainingMs <= 0) {
				throw new Error(`Streaming did not become ready within ${timeoutMs}ms`);
			}
			// En retry damos más margen a RTP; en gate normal seguimos ≤3s.
			const mediaCapMs = this.streamingRetryInProgress ? 10_000 : 3_000;
			const mediaTimeoutMs = Math.min(mediaCapMs, remainingMs);
			await this.webRTCStreamingService.waitForOutboundMedia(mediaTimeoutMs);
		} catch (err: unknown) {
			const message = err instanceof Error ? err.message : String(err);
			this.lastStreamingReadyFailure = {
				atMs: Date.now(),
				message,
				hasLiveOutboundVideo: this.webRTCStreamingService.hasLiveOutboundVideo(),
				iceConnectionState: this.webRTCStreamingService.getIceConnectionState(),
				streamingStatus: this.getCurrentStreamingStatus(),
			};
			throw err;
		}
	}

	/** Último fallo de waitForStreamingReady (null si aún no falló). */
	public getLastStreamingReadyFailure(): StreamingReadyFailure | null {
		return this.lastStreamingReadyFailure;
	}

	/** Track de vídeo outbound live en el sender WHIP. */
	public hasLiveOutboundVideo(): boolean {
		return this.webRTCStreamingService.hasLiveOutboundVideo();
	}

	/** True si hay un RETRY de MEDIA_SERVER en curso. */
	public isStreamingRetryInProgress(): boolean {
		return this.streamingRetryInProgress;
	}

	/**
	 * Espera a que termine un `retryStreaming` en curso (OCR/POL no deben competir con waitReady).
	 */
	public async waitUntilStreamingRetryIdle(timeoutMs: number = 30_000): Promise<void> {
		if (!this.streamingRetryInProgress) {
			return;
		}
		const startedAt = Date.now();
		while (this.streamingRetryInProgress) {
			if (Date.now() - startedAt > timeoutMs) {
				throw new Error('Timed out waiting for streaming retry to finish');
			}
			await new Promise((r) => setTimeout(r, 50));
		}
	}

	/**
	 * Reintenta WHIP tras fallo MEDIA_SERVER: sale de ERROR, restaura UI/cámara si hace falta,
	 * vuelve a publicar y espera outbound listo.
	 * @param restoreCameraUi p. ej. clearError para remontar OCR/POL y recuperar getUserMedia
	 */
	public async retryStreaming(restoreCameraUi?: () => void): Promise<void> {
		const streamId = this.currentOperationStreamId ?? this.baseStreamId;
		if (!streamId) {
			throw new Error('Cannot retry streaming: missing streamId');
		}

		// Flag ANTES de clearError: el OCR remonta y no debe lanzar otro waitReady/tile.
		this.streamingRetryInProgress = true;
		try {
			// Evitar que waiters vean ERROR/STREAMING stale del intento fallido.
			this.streamingStatusSubject.next(StreamingStatus.STARTING);
			this.isStreamingActiveSubject.next(false);

			restoreCameraUi?.();

			const stream = await this.waitForActiveCameraStream(15_000);
			// Forzar PC nuevo sin matar tracks (disconnect() haría track.stop()).
			this.webRTCStreamingService.resetPeerConnectionForRepublish();
			const ok = await this.startStreaming(streamId, stream);
			if (!ok) {
				throw new Error('Failed to restart streaming on retry');
			}
			// Tras remount la cámara/RTP puede tardar más que el gate normal (3s).
			await this.waitForStreamingReady(20_000);
		} finally {
			this.streamingRetryInProgress = false;
		}
	}

	private async waitForActiveCameraStream(timeoutMs: number): Promise<MediaStream> {
		const current = this.cameraService.stream;
		if (current?.active) {
			return current;
		}

		return new Promise<MediaStream>((resolve, reject) => {
			const timer = setTimeout(() => {
				sub.unsubscribe();
				reject(new Error('Camera stream not available for streaming retry'));
			}, timeoutMs);
			const sub = this.cameraService.stream$.subscribe((stream) => {
				if (stream?.active) {
					clearTimeout(timer);
					sub.unsubscribe();
					resolve(stream);
				}
			});
		});
	}

	/** WHIP 201 deja STREAMING; CONNECTED llega cuando ICE/connectionState conecta. */
	private isWebRtcPublishReady(status: WebRTCStatus): boolean {
		return status === WebRTCStatus.CONNECTED || status === WebRTCStatus.STREAMING;
	}

	private async waitForWebRtcConnected(timeoutMs: number): Promise<void> {
		if (this.isStreamingActive() && this.getCurrentStreamingStatus() === StreamingStatus.ACTIVE) {
			const currentWebRTCStatus = await firstValueFrom(this.webRTCStreamingService.getStatus());
			if (this.isWebRtcPublishReady(currentWebRTCStatus)) {
				return;
			}
		}

		return new Promise((resolve, reject) => {
			let subscription: Subscription | undefined;
			let webRTCSubscription: Subscription | undefined;

			const timeout = setTimeout(() => {
				subscription?.unsubscribe();
				webRTCSubscription?.unsubscribe();
				reject(new Error(`Streaming did not become ready within ${timeoutMs}ms`));
			}, timeoutMs);

			let streamingIsActive = false;
			let webRTCIsConnected = false;
			// No fallar por ERROR/FAILED ya presente al suscribirse (reintento tras tile MEDIA_SERVER).
			let allowStreamingErrorReject = this.streamingStatusSubject.value !== StreamingStatus.ERROR;
			let skipInitialWebRtcFailure = true;

			const checkBothReady = () => {
				if (streamingIsActive && webRTCIsConnected) {
					clearTimeout(timeout);
					subscription?.unsubscribe();
					webRTCSubscription?.unsubscribe();
					resolve();
				}
			};

			subscription = this.streamingStatusSubject.subscribe((status) => {
				if (status === StreamingStatus.ACTIVE && this.isStreamingActive()) {
					streamingIsActive = true;
					allowStreamingErrorReject = true;
					checkBothReady();
				} else if (status === StreamingStatus.ERROR) {
					if (allowStreamingErrorReject) {
						clearTimeout(timeout);
						subscription?.unsubscribe();
						webRTCSubscription?.unsubscribe();
						reject(new Error('Streaming encountered an error'));
					}
				} else {
					allowStreamingErrorReject = true;
				}
			});

			webRTCSubscription = this.webRTCStreamingService.getStatus().subscribe((webRTCStatus) => {
				if (this.isWebRtcPublishReady(webRTCStatus)) {
					skipInitialWebRtcFailure = false;
					webRTCIsConnected = true;
					checkBothReady();
				} else if (webRTCStatus === WebRTCStatus.FAILED || webRTCStatus === WebRTCStatus.ERROR) {
					if (skipInitialWebRtcFailure) {
						skipInitialWebRtcFailure = false;
						return;
					}
					clearTimeout(timeout);
					subscription?.unsubscribe();
					webRTCSubscription?.unsubscribe();
					reject(new Error('WebRTC connection failed'));
				} else {
					skipInitialWebRtcFailure = false;
				}
			});
		});
	}

	/**
	 * Verifica si la grabación está habilitada en la configuración
	 */
	private checkRecordingRequired(): boolean {
		try {
			const config = this.configService.getCurrentConfig();
			return config?.required_recording || false;
		} catch (error) {
			return false;
		}
	}

	/**
	 * Verifica si la grabación es requerida (método público)
	 */
	public isRecordingRequired(): boolean {
		return this.checkRecordingRequired();
	}

	private getWebRTCConfig() {
		return {
			enableAudio: true,
			enableVideo: true,
			bandwidth: 900,
			debug: false, // Cambiar a true para habilitar logs detallados
			offerToReceiveAudio: false, // No recibir audio (streaming unidireccional)
			offerToReceiveVideo: false, // No recibir video (streaming unidireccional)
		};
	}

	private setupWebRTCStatusMonitoring(): void {
		this.webRTCStreamingService
			.getStatus()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (status: WebRTCStatus) => {
					// Manejar cambios de estado
					switch (status) {
						case WebRTCStatus.CONNECTED:
							if (this.streamingStatusSubject.value === StreamingStatus.STARTING) {
								this.streamingStatusSubject.next(StreamingStatus.ACTIVE);
							}
							break;
						case WebRTCStatus.DISCONNECTED:
						case WebRTCStatus.FAILED:
							if (this.isStreamingActive()) {
								this.streamingStatusSubject.next(StreamingStatus.ERROR);
								this.isStreamingActiveSubject.next(false);
							}
							break;
						case WebRTCStatus.ERROR:
							this.streamingStatusSubject.next(StreamingStatus.ERROR);
							break;
					}
				},
				error: (error) => {
					this.cloudLogsService.sendCloudLogs(
						{ streaming: `WebRTC status monitoring error: ${error?.toString()}` },
						ProcessLog.IN_PROCESS,
						LogType.Error,
					);
				},
			});
	}

	/**
	 * Pausa el streaming
	 */
	// REMOVIDO - NO UTILIZADO

	/**
	 * Reanuda el streaming desde pausa
	 */
	// REMOVIDO - NO UTILIZADO

	/**
	 * Reinicia el streaming después de un cambio de cámara.
	 * Se detiene completamente y se reinicia con el nuevo stream de cámara.
	 * Este método asume que el nuevo stream ya está disponible en CameraService.
	 *
	 * Nota: Este método debe ser llamado DESPUÉS de stopStreaming() en changeCamera().
	 * En ese momento, isStreamingActive() será false, así que verificamos si hay
	 * un stream válido en lugar de checar el estado.
	 */
	public async restartStreamingAfterCameraChange(): Promise<void> {
		try {
			// 1. Obtener el nuevo stream de la cámara
			const newStream = this.cameraService.stream;
			if (!newStream || !newStream.active) return;

			// Esperar un poco a que el stream esté completamente inicializado
			await new Promise((resolve) => setTimeout(resolve, 500));

			// 2. Reinitialize WebRTC connection with new stream
			const deviceId = this.cameraService.getCurrentDeviceId();

			// Preservar el streamId de la operación actual (POL/OCR)
			// para que el media server reciba el mismo streamId durante cambios de cámara
			const preservedStreamId = this.currentOperationStreamId;

			if (!preservedStreamId) {
				this.cloudLogsService.sendCloudLogs(
					{ streaming: 'Cannot restart streaming: no preserved streamId' },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
				return;
			}

			const result = await this.webRTCStreamingService.initializeConnection(
				newStream,
				deviceId,
				this.getWebRTCConfig(),
				preservedStreamId, // Usar el streamId preservado de la operación
			);

			if (result) {
				this.isStreamingActiveSubject.next(true);
			} else {
				throw new Error('Failed to reinitialize WebRTC connection after camera change');
			}
		} catch (error) {
			this.cloudLogsService.sendCloudLogs(
				{
					streaming: 'Error restarting streaming after camera change',
					error: error?.toString(),
				},
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
		}
	}

	/**
	 * Configura los handlers para eventos de navegación/cierre
	 */
	private setupPageUnloadHandlers(): void {
		window.addEventListener('beforeunload', this.onBeforeUnload);
		window.addEventListener('unload', this.onUnload);
	}

	/**
	 * Cleanup de emergencia
	 */
	private emergencyCleanup(): void {
		try {
			// Log del evento
			this.cloudLogsService.sendCloudLogs(
				{ streaming: 'Emergency cleanup triggered - page closing' },
				ProcessLog.FINISH,
				LogType.Warning,
			);

			// Enviar comando de stop al media server (sincrónico)
			this.webRTCStreamingService.emergencyDisconnect();

			// Actualizar estados internos
			this.isStreamingActiveSubject.next(false);
			this.streamingStatusSubject.next(StreamingStatus.IDLE);

			// Limpiar recursos locales
			this.destroy$.next();
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error during emergency cleanup: ${error?.toString()}` },
				ProcessLog.FINISH,
				LogType.Error,
			);
		}
	}

	/**
	 * Actualiza y almacena el identificador base del stream cuando se carga la configuración
	 * Se debe llamar desde getConfig() y updateConfig() en el componente de identificación
	 *
	 * @param process - La configuración de respuesta del proceso que contiene process.process
	 */
	public setBaseStreamId(processId?: string): void {
		if (processId) this.baseStreamId = processId;
	}

	/**
	 * Obtiene el identificador base del stream almacenado en caché
	 * @returns El identificador base, o null si no ha sido establecido
	 */
	public getBaseStreamId(): string | null {
		return this.baseStreamId;
	}

	/**
	 * Elimina los listeners de eventos del navegador
	 */
	private removePageUnloadHandlers(): void {
		window.removeEventListener('beforeunload', this.onBeforeUnload);
		window.removeEventListener('unload', this.onUnload);
	}

	public async destroy(): Promise<void> {
		// Detener streaming ANTES de completar los suscriptores
		if (this.isStreamingActive()) {
			await this.stopStreaming();
		}

		// Eliminar listeners de eventos del navegador
		this.removePageUnloadHandlers();

		this.destroy$.next();
		this.destroy$.complete();
	}
}
